import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonBase } from '@mui/material';
import {
    RM3_BED,
    RM3_BOARD,
    RM3_BOARD_ORDER,
    RM3_FILTER,
    RM3_RIBBON,
    RM3_RIBBON_ORDER,
} from 'arbitrip-common/client/utils/HotelDealsRm3';

import { ReactComponent as FreeCancelationIcon } from '@/img/results_page/free_cancelation_icon.svg';

import { RM3_ACTION, activeFilterCount } from './rm3FiltersReducer';
import { RIBBON_I18N_SUFFIX } from './Rm3Ribbon';
import { BED_ICON_KIND, IconBedKind, IconBoard, IconChevronDown, IconReset, IconTopPick } from './Rm3Icons';
import { BOARD_LABEL_KEYS } from './rm3BoardLabels';

const PILL_ICON_SIZE = 16;
const FIT_TOLERANCE_PX = 0.5;

const BOARD_PILL_TESTIDS = {
    [RM3_BOARD.ROOM_ONLY]: 'rm3-filter-room-only',
    [RM3_BOARD.BREAKFAST]: 'rm3-filter-breakfast',
    [RM3_BOARD.HALF_BOARD]: 'rm3-filter-half-board',
    [RM3_BOARD.FULL_BOARD]: 'rm3-filter-full-board',
    [RM3_BOARD.ALL_INCLUSIVE]: 'rm3-filter-all-inclusive',
};

const BED_PILLS = [
    {
        value: RM3_BED.KING,
        labelKey: 'HotelDealsRm3.FilterKingBed',
        testid: 'rm3-filter-king',
        iconKind: BED_ICON_KIND.DOUBLE,
    },
    {
        value: RM3_BED.QUEEN,
        labelKey: 'HotelDealsRm3.FilterQueenBed',
        testid: 'rm3-filter-queen',
        iconKind: BED_ICON_KIND.DOUBLE,
    },
    {
        value: RM3_BED.DOUBLE,
        labelKey: 'HotelDealsRm3.FilterDoubleBed',
        testid: 'rm3-filter-double',
        iconKind: BED_ICON_KIND.DOUBLE,
    },
    {
        value: RM3_BED.TWIN,
        labelKey: 'HotelDealsRm3.FilterTwinBed',
        testid: 'rm3-filter-twin',
        iconKind: BED_ICON_KIND.TWIN,
    },
];

function Pill({ on, onClick, testid, className = '', children }) {
    return (
        <ButtonBase
            className={`rm3-pill${on ? ' rm3-pill--on' : ''} ${className}`}
            onClick={onClick}
            aria-pressed={on}
            data-testid={testid}
            disableRipple
        >
            {children}
        </ButtonBase>
    );
}

/** A labelled pill group (spec-v2 §2.1); a group with no pill to show renders nothing, not even its heading. */
function FilterGroup({ heading, testid, full, fit, overflow, children }) {
    const pills = React.Children.toArray(children).filter(Boolean);
    if (!pills.length) {
        return null;
    }
    return (
        <div
            className={`rm3-filter-group${full ? ' rm3-filter-group--full' : ''}${
                overflow ? ' rm3-filter-group--overflow' : ''
            }`}
            data-testid={testid}
            data-fit-group={fit ? '' : undefined}
            data-overflow={fit ? String(!!overflow) : undefined}
        >
            <span className="rm3-filter-group__heading">{heading}</span>
            <div className="rm3-filter-group__pills">{pills}</div>
        </div>
    );
}

/**
 * Desktop filters in a bordered box (spec-v2 §2, delta-2 §1): collapsed, the groups that fit the row in order;
 * the rest and Top picks only while expanded or while one of their chips is on. Single-select groups clear on a
 * second click; In policy / Pay at hotel keep today's visibility conditions; Reset (filters only, not sort) is counted.
 */
function Rm3FilterBar({ state, dispatch, topPicks, available, showInPolicy, showPayAtHotel, anyFilterOn }) {
    const { t } = useTranslation();
    const rowsId = useId();
    // Local, not in the reducer, so Reset never collapses the panel.
    const [expanded, setExpanded] = useState(false);
    const [fitCount, setFitCount] = useState(1);
    const rowRef = useRef(null);
    const { filters } = state;
    // A chip that can match nothing is not offered; an active chip always stays so it can be turned off.
    const freeCancellationOn = filters[RM3_FILTER.FREE_CANCELLATION];
    const inPolicyOn = filters[RM3_FILTER.IN_POLICY];
    const payAtHotelOn = filters[RM3_FILTER.PAY_AT_HOTEL];
    const activeTopPicks = filters[RM3_FILTER.TOP_PICKS];
    const topPickPills = RM3_RIBBON_ORDER.filter((kind) => topPicks.includes(kind) || activeTopPicks.includes(kind));
    const boardPills = RM3_BOARD_ORDER.filter(
        (board) => filters[RM3_FILTER.BOARD] === board || available[RM3_FILTER.BOARD][board],
    );
    const bedPills = BED_PILLS.filter(
        (pill) => filters[RM3_FILTER.BED] === pill.value || available[RM3_FILTER.BED][pill.value],
    );
    const showFreeCancellation = freeCancellationOn || available[RM3_FILTER.FREE_CANCELLATION];
    const showInPolicyPill = inPolicyOn || (showInPolicy && available[RM3_FILTER.IN_POLICY]);
    const showPayAtHotelPill = payAtHotelOn || (showPayAtHotel && available[RM3_FILTER.PAY_AT_HOTEL]);

    const fitGroups = [
        { id: 'cancellation', pills: showFreeCancellation ? ['free'] : [], active: freeCancellationOn },
        { id: 'meal-plan', pills: boardPills, active: filters[RM3_FILTER.BOARD] !== null },
        { id: 'bed', pills: bedPills.map((pill) => pill.value), active: filters[RM3_FILTER.BED] !== null },
        {
            id: 'policy-payment',
            pills: [showInPolicyPill && 'in-policy', showPayAtHotelPill && 'pay-at-hotel'].filter(Boolean),
            active: inPolicyOn || payAtHotelOn,
        },
    ].filter((group) => group.pills.length > 0);
    const fitKey = fitGroups.map((group) => `${group.id}:${group.pills.join(',')}`).join('|');
    const hiddenFitGroups = fitGroups.slice(fitCount);

    const hiddenGroupActive = hiddenFitGroups.some((group) => group.active) || activeTopPicks.length > 0;
    const open = expanded || hiddenGroupActive;
    const hiddenGroupHasPills = hiddenFitGroups.length > 0 || topPickPills.length > 0;
    // While a hidden chip is on the panel cannot collapse without hiding it, so Reset is the way out.
    const showToggle = hiddenGroupHasPills && !hiddenGroupActive;
    const overflowIds = new Set(open ? [] : hiddenFitGroups.map((group) => group.id));

    // Widths are measured with every fit group mounted, so the prefix does not depend on what is hidden.
    useLayoutEffect(() => {
        const row = rowRef.current;
        if (!row) {
            return undefined;
        }
        const groups = Array.from(row.querySelectorAll('[data-fit-group]'));
        const measure = () => {
            const avail = row.clientWidth;
            const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
            let used = 0;
            let count = 0;
            for (let i = 0; i < groups.length; i += 1) {
                used += (i ? gap : 0) + groups[i].getBoundingClientRect().width;
                if (i > 0 && used > avail + FIT_TOLERANCE_PX) {
                    break;
                }
                count = i + 1;
            }
            const next = Math.max(1, count);
            setFitCount((current) => (current === next ? current : next));
        };
        measure();
        if (typeof ResizeObserver !== 'function') {
            return undefined;
        }
        let frame = 0;
        const observer = new ResizeObserver(() => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(measure);
        });
        observer.observe(row);
        groups.forEach((group) => observer.observe(group));
        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
        };
    }, [fitKey]);

    // Active chips always show, so with no pill at all no filter is on either: no empty box.
    if (!fitGroups.length && !topPickPills.length) {
        return null;
    }

    return (
        <div className="rm3-filter-bar" data-testid="rm3-filter-bar">
            <div className="rm3-filter-rows" id={rowsId} ref={rowRef}>
                <FilterGroup
                    heading={t('HotelDealsRm3.SectionCancellation')}
                    testid="rm3-filter-group-cancellation"
                    fit
                    overflow={overflowIds.has('cancellation')}
                >
                    {showFreeCancellation && (
                        <Pill
                            on={freeCancellationOn}
                            onClick={() => dispatch({ type: RM3_ACTION.TOGGLE_FREE_CANCELLATION })}
                            testid="rm3-filter-free-cancellation"
                        >
                            <FreeCancelationIcon
                                className="rm3-pill__icon"
                                width={PILL_ICON_SIZE}
                                height={PILL_ICON_SIZE}
                                aria-hidden="true"
                                focusable="false"
                            />
                            {t('HotelDealsRm3.FilterFreeCancellation')}
                        </Pill>
                    )}
                </FilterGroup>
                <FilterGroup
                    heading={t('HotelDealsRm3.SectionMealPlan')}
                    testid="rm3-filter-group-meal-plan"
                    fit
                    overflow={overflowIds.has('meal-plan')}
                >
                    {boardPills.map((board) => (
                        <Pill
                            key={board}
                            on={filters[RM3_FILTER.BOARD] === board}
                            onClick={() => dispatch({ type: RM3_ACTION.SELECT_BOARD, value: board })}
                            testid={BOARD_PILL_TESTIDS[board]}
                        >
                            <span className="rm3-pill__icon">
                                <IconBoard board={board} size={PILL_ICON_SIZE} />
                            </span>
                            {t(BOARD_LABEL_KEYS[board])}
                        </Pill>
                    ))}
                </FilterGroup>
                <FilterGroup
                    heading={t('HotelDealsRm3.SectionBedType')}
                    testid="rm3-filter-group-bed"
                    fit
                    overflow={overflowIds.has('bed')}
                >
                    {bedPills.map((pill) => (
                        <Pill
                            key={pill.value}
                            on={filters[RM3_FILTER.BED] === pill.value}
                            onClick={() => dispatch({ type: RM3_ACTION.SELECT_BED, value: pill.value })}
                            testid={pill.testid}
                        >
                            <span className="rm3-pill__icon">
                                <IconBedKind kind={pill.iconKind} size={PILL_ICON_SIZE} />
                            </span>
                            {t(pill.labelKey)}
                        </Pill>
                    ))}
                </FilterGroup>
                <FilterGroup
                    heading={t('HotelDealsRm3.SectionPolicyPayment')}
                    testid="rm3-filter-group-policy-payment"
                    fit
                    overflow={overflowIds.has('policy-payment')}
                >
                    {showInPolicyPill && (
                        <Pill
                            on={inPolicyOn}
                            onClick={() => dispatch({ type: RM3_ACTION.TOGGLE_IN_POLICY })}
                            testid="rm3-filter-in-policy"
                        >
                            {t('HotelDealsRm3.FilterInPolicy')}
                        </Pill>
                    )}
                    {showPayAtHotelPill && (
                        <Pill
                            on={payAtHotelOn}
                            onClick={() => dispatch({ type: RM3_ACTION.TOGGLE_PAY_AT_HOTEL })}
                            testid="rm3-filter-pay-at-hotel"
                        >
                            {t('HotelDealsRm3.FilterPayAtHotel')}
                        </Pill>
                    )}
                </FilterGroup>
                {open && (
                    <>
                        <FilterGroup
                            heading={t('HotelDealsRm3.SectionFilterByTopPicks')}
                            testid="rm3-filter-group-top-picks"
                            full
                        >
                            {topPickPills.map((kind) => (
                                <Pill
                                    key={kind}
                                    on={activeTopPicks.includes(kind)}
                                    onClick={() => dispatch({ type: RM3_ACTION.TOGGLE_TOP_PICK, value: kind })}
                                    testid={`rm3-top-pick-${kind}`}
                                    className="rm3-pill--top-pick"
                                >
                                    <span
                                        className={`rm3-pill__icon rm3-pill__icon--top-pick${
                                            kind === RM3_RIBBON.LOW ? ' rm3-pill__icon--low' : ''
                                        }`}
                                    >
                                        <IconTopPick kind={kind} size={PILL_ICON_SIZE} />
                                    </span>
                                    {t(`HotelDealsRm3.TopPick.${RIBBON_I18N_SUFFIX[kind]}`)}
                                </Pill>
                            ))}
                        </FilterGroup>
                    </>
                )}
            </div>
            {(showToggle || anyFilterOn) && (
                <div className="rm3-filter-footer">
                    {showToggle && (
                        <ButtonBase
                            className="rm3-filters-toggle"
                            onClick={() => setExpanded((previous) => !previous)}
                            aria-expanded={open}
                            aria-controls={rowsId}
                            data-testid="rm3-filters-toggle"
                            disableRipple
                        >
                            {t(open ? 'HotelDealsRm3.ShowFewerFilters' : 'HotelDealsRm3.ShowAllFilters')}
                            <IconChevronDown
                                className={`rm3-filters-toggle__chevron${
                                    open ? ' rm3-filters-toggle__chevron--open' : ''
                                }`}
                            />
                        </ButtonBase>
                    )}
                    {anyFilterOn && (
                        <ButtonBase
                            className="rm3-reset"
                            onClick={() => dispatch({ type: RM3_ACTION.RESET_FILTERS })}
                            data-testid="rm3-reset-filters"
                            disableRipple
                        >
                            {t('HotelDealsRm3.ResetFiltersCount', { count: activeFilterCount(filters) })}
                            <IconReset />
                        </ButtonBase>
                    )}
                </div>
            )}
        </div>
    );
}

export default Rm3FilterBar;
